/**
 * Dashboard-level action items board. One query per dashboard; filtering is
 * client-side over the active set. Feature-gated on `action_items`.
 */

import React, { useMemo, useState } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Header from '@amzn/awsui-components-core/header';
import Link from '@amzn/awsui-components-core/link';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Modal from '@amzn/awsui-components-core/modal';
import Table from '@amzn/awsui-components-core/table';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import {
  selectFeatureAccessible,
  useGetPermissionsQuery,
} from 'src/features/api/permissionsApi';
import {
  useCreateGlobalActionItemMutation,
  useDeleteActionItemMutation,
  useGetDashboardActionItemsQuery,
  useUpdateActionItemMutation,
} from 'src/features/api/actionItemsApi';
import {
  ActionItemRecord,
  ActionItemStatus,
  ActionItemType,
} from 'src/types/actionItems.types';
import { ACTION_ITEMS_FEATURE_ID } from 'src/constants/actionItems';
import { ActionItemForm } from './ActionItemForm';
import { ActionItemStatusIndicator, STATUS_LABELS } from './ActionItemStatus';
import { ActionItemDetail } from './ActionItemDetail';
import { ETA_BUCKET_LABELS, EtaBucket, etaBucket, isOverdue } from './etaUtils';

const TYPE_LABELS: Record<ActionItemType, string> = {
  CALLOUT_THREAD: 'Callout Based',
  GLOBAL: 'General',
};

// Cloudscape Select has no clear affordance, so "All" is a real option.
const ALL = { value: '', label: 'All' };

type FormState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; record: ActionItemRecord }
  | { mode: 'detail'; record: ActionItemRecord }
  | { mode: 'confirm-delete'; record: ActionItemRecord };

export const ActionItemsBoard: React.FC = () => {
  const { dashboardId } = useActiveDashboard();
  const {
    data: permissions,
    isSuccess: permissionsResolved,
    isError: permissionsFailed,
  } = useGetPermissionsQuery();
  const accessible = selectFeatureAccessible(
    permissions,
    ACTION_ITEMS_FEATURE_ID,
  );

  const {
    data: items,
    isFetching,
    isError: listFailed,
  } = useGetDashboardActionItemsQuery(dashboardId, { skip: !accessible });
  const [createGlobal] = useCreateGlobalActionItemMutation();
  const [update] = useUpdateActionItemMutation();
  const [remove, { isLoading: deleting }] = useDeleteActionItemMutation();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [etaFilter, setEtaFilter] = useState('');
  const [form, setForm] = useState<FormState>({ mode: 'closed' });

  // Distinct assignees across the loaded items, for the filter's options.
  const assignees = useMemo(
    () =>
      Array.from(
        new Set((items ?? []).map((i) => i.assignee).filter(Boolean)),
      ).sort() as string[],
    [items],
  );

  const visible = useMemo(
    () =>
      (items ?? []).filter(
        (item) =>
          (!typeFilter || item.type === typeFilter) &&
          (!statusFilter || item.status === statusFilter) &&
          (!assigneeFilter || item.assignee === assigneeFilter) &&
          (!etaFilter || etaBucket(item) === etaFilter),
      ),
    [items, typeFilter, statusFilter, assigneeFilter, etaFilter],
  );

  // Render nothing while permissions resolve so users with access never see
  // the denial flash.
  if (!permissionsResolved && !permissionsFailed) {
    return null;
  }

  // A failed permissions fetch is not a denial.
  if (permissionsFailed) {
    return (
      <Box padding="l" data-testid="action-items-permissions-error">
        <Header variant="h2">Action Items</Header>
        <Box color="text-status-error">
          Couldn’t check your access to Action Items. Reload the page to try
          again.
        </Box>
      </Box>
    );
  }

  if (!accessible) {
    return (
      <Box padding="l" data-testid="action-items-no-access">
        <Header variant="h2">Action Items</Header>
        <Box color="text-status-inactive">
          You do not have access to Action Items on this dashboard.
        </Box>
      </Box>
    );
  }

  return (
    <Box padding="l" data-testid="action-items-board">
      {/* Rows open the detail, so they get a pointer — but only when there
          are rows; the empty state is not clickable. */}
      <div
        className={
          visible.length > 0
            ? 'action-items-board action-items-board--clickable'
            : 'action-items-board'
        }
        data-testid="action-items-table"
      >
        <Table
          header={
            <Header
              variant="h2"
              counter={items ? `(${visible.length})` : undefined}
              actions={
                <Button
                  variant="primary"
                  onClick={() => setForm({ mode: 'create' })}
                  data-testid="action-items-create"
                >
                  Create action item
                </Button>
              }
            >
              Action Items
            </Header>
          }
          filter={
            /* The site's own filter row (align-items:center) keeps the labeled
             selects and the plain input on one baseline. */
            <div className="filter-shell__primaries action-items__filters">
              <div className="filter-shell__pill">
                <Select
                  inlineLabelText="Type"
                  selectedOption={
                    typeFilter
                      ? {
                          value: typeFilter,
                          label: TYPE_LABELS[typeFilter as ActionItemType],
                        }
                      : ALL
                  }
                  options={[
                    ALL,
                    ...Object.entries(TYPE_LABELS).map(([value, label]) => ({
                      value,
                      label,
                    })),
                  ]}
                  onChange={({ detail }) =>
                    setTypeFilter(detail.selectedOption.value ?? '')
                  }
                  data-testid="action-items-filter-type"
                />
              </div>
              <div className="filter-shell__pill">
                <Select
                  inlineLabelText="Status"
                  selectedOption={
                    statusFilter
                      ? {
                          value: statusFilter,
                          label:
                            STATUS_LABELS[statusFilter as ActionItemStatus],
                        }
                      : ALL
                  }
                  options={[
                    ALL,
                    ...Object.entries(STATUS_LABELS).map(([value, label]) => ({
                      value,
                      label,
                    })),
                  ]}
                  onChange={({ detail }) =>
                    setStatusFilter(detail.selectedOption.value ?? '')
                  }
                  data-testid="action-items-filter-status"
                />
              </div>
              <div className="filter-shell__pill">
                <Select
                  inlineLabelText="Assignee"
                  selectedOption={
                    assigneeFilter
                      ? { value: assigneeFilter, label: assigneeFilter }
                      : ALL
                  }
                  options={[
                    ALL,
                    ...assignees.map((a) => ({ value: a, label: a })),
                  ]}
                  onChange={({ detail }) =>
                    setAssigneeFilter(detail.selectedOption.value ?? '')
                  }
                  data-testid="action-items-filter-assignee"
                />
              </div>
              <div className="filter-shell__pill">
                <Select
                  inlineLabelText="ETA"
                  selectedOption={
                    etaFilter
                      ? {
                          value: etaFilter,
                          label: ETA_BUCKET_LABELS[etaFilter as EtaBucket],
                        }
                      : ALL
                  }
                  options={[
                    ALL,
                    ...Object.entries(ETA_BUCKET_LABELS).map(
                      ([value, label]) => ({ value, label }),
                    ),
                  ]}
                  onChange={({ detail }) =>
                    setEtaFilter(detail.selectedOption.value ?? '')
                  }
                  data-testid="action-items-filter-eta"
                />
              </div>
            </div>
          }
          loading={isFetching}
          items={visible}
          trackBy="id"
          onRowClick={({ detail }) =>
            setForm({ mode: 'detail', record: detail.item })
          }
          columnDefinitions={[
            {
              id: 'heading',
              header: 'Heading',
              // Bounded so a long heading never distorts the table layout.
              minWidth: 200,
              maxWidth: 340,
              cell: (item) => (
                <Link
                  onFollow={() => setForm({ mode: 'detail', record: item })}
                  data-testid="action-item-open-detail"
                >
                  <span
                    className="action-items-board__heading"
                    data-overdue={isOverdue(item) ? 'true' : undefined}
                  >
                    {item.heading}
                  </span>
                </Link>
              ),
            },
            {
              id: 'type',
              header: 'Type',
              minWidth: 130,
              cell: (item) => TYPE_LABELS[item.type],
            },
            {
              id: 'status',
              header: 'Status',
              minWidth: 130,
              cell: (item) => (
                <ActionItemStatusIndicator status={item.status} />
              ),
            },
            {
              id: 'assignee',
              header: 'Assignee',
              cell: (item) => item.assignee ?? '—',
            },
            {
              id: 'eta',
              header: 'ETA',
              cell: (item) => item.eta ?? '—',
            },
            {
              id: 'actions',
              header: 'Actions',
              minWidth: 140,
              cell: (item) => (
                // Row clicks open the detail; the inline actions must not.
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div
                  className="action-items-board__actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="inline-link"
                    onClick={() => setForm({ mode: 'edit', record: item })}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="inline-link"
                    onClick={() => {
                      setDeleteError(null);
                      setForm({ mode: 'confirm-delete', record: item });
                    }}
                  >
                    Delete
                  </Button>
                </div>
              ),
            },
          ]}
          empty={
            listFailed ? (
              <Box
                textAlign="center"
                color="text-status-error"
                data-testid="action-items-list-error"
              >
                Couldn’t load action items. Reload the page to try again.
              </Box>
            ) : (
              <Box textAlign="center" color="text-status-inactive">
                {(items ?? []).length === 0
                  ? 'No action items yet.'
                  : 'No action items match the current filters.'}
              </Box>
            )
          }
        />
      </div>
      {form.mode === 'create' && (
        <ActionItemForm
          title="Create action item"
          existing={null}
          onSubmit={(draft) => createGlobal({ dashboardId, draft }).unwrap()}
          onDismiss={() => setForm({ mode: 'closed' })}
        />
      )}
      {form.mode === 'confirm-delete' && (
        <Modal
          visible={true}
          size="small"
          onDismiss={() => setForm({ mode: 'closed' })}
          closeAriaLabel="Close"
          header="Delete action item"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  disabled={deleting}
                  onClick={() => setForm({ mode: 'closed' })}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={deleting}
                  disabled={deleting}
                  data-testid="action-item-confirm-delete"
                  onClick={async () => {
                    setDeleteError(null);
                    try {
                      await remove({ existing: form.record }).unwrap();
                      setForm({ mode: 'closed' });
                    } catch {
                      // Keep the dialog open; the delete did not happen.
                      setDeleteError(
                        'Could not delete the action item. Please try again.',
                      );
                    }
                  }}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <span>
              Permanently delete “{form.record.heading}”? This cannot be undone.
            </span>
            {deleteError && (
              <Box color="text-status-error" fontSize="body-s">
                {deleteError}
              </Box>
            )}
          </SpaceBetween>
        </Modal>
      )}
      {form.mode === 'detail' && (
        <ActionItemDetail
          record={form.record}
          onDismiss={() => setForm({ mode: 'closed' })}
          onEdit={() => setForm({ mode: 'edit', record: form.record })}
        />
      )}
      {form.mode === 'edit' && (
        <ActionItemForm
          title="Edit action item"
          existing={form.record}
          onSubmit={(draft) =>
            update({ existing: form.record, draft }).unwrap()
          }
          onDismiss={() => setForm({ mode: 'closed' })}
        />
      )}
    </Box>
  );
};
